import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Lock, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "sonner";

type Mode = "login" | "signup";

// Both naming conventions are accepted so older call sites
// (`open` / `onClose` / `defaultMode`) keep working while newer ones
// can use the shadcn-style (`isOpen` / `onOpenChange` / `initialMode`
// / `mode`). All "new" props are optional and fall back to the legacy
// ones — call sites must still provide at least one open-state prop
// and one close callback.
interface AuthModalProps {
  /** Legacy: controlled open flag. */
  open?: boolean;
  /** Alias of `open`. */
  isOpen?: boolean;
  /** Legacy: called when the modal requests to close. */
  onClose?: () => void;
  /** Alias of `onClose` — receives the next open state (shadcn-style). */
  onOpenChange?: (open: boolean) => void;
  /** Legacy: which tab to show on first open. */
  defaultMode?: Mode;
  /** Alias of `defaultMode`. */
  initialMode?: Mode;
  /** Alias of `defaultMode`. */
  mode?: Mode;
}

const AuthModal = (props: AuthModalProps) => {
  const {
    open,
    isOpen,
    onClose,
    onOpenChange,
    defaultMode,
    initialMode,
    mode: modeProp,
  } = props;

  const isOpenResolved = open ?? isOpen ?? false;
  const initialModeResolved: Mode =
    defaultMode ?? initialMode ?? modeProp ?? "login";
  const handleClose = () => {
    onClose?.();
    onOpenChange?.(false);
  };

  const [mode, setMode] = useState<Mode>(initialModeResolved);
  const navigate = useNavigate();

  // Reset mode whenever the modal is (re)opened so a "Join" CTA always lands
  // on signup, even if the user previously toggled to login and closed it.
  useEffect(() => {
    if (isOpenResolved) setMode(initialModeResolved);
  }, [isOpenResolved, initialModeResolved]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Mark this device as a "returning visitor" once they successfully
  // authenticate. Navbar reads this flag to flip Sign in / Join primary
  // CTA on subsequent visits.
  const markReturning = () => {
    try {
      window.localStorage.setItem("hl_returning", "1");
    } catch {
      /* noop — private mode */
    }
  };

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "signup") {
      // Public signup is closed — invite required.
      handleClose();
      navigate("/signup");
      return;
    }
    setLoading(true);
    {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) toast.error(error.message);
      else {
        markReturning();
        toast.success("Welcome back!");
        handleClose();
      }
    }
    setLoading(false);
  };

  const handleOAuth = async (provider: "google" | "apple") => {
    const label = provider === "google" ? "Google" : "Apple";
    const result = await lovable.auth.signInWithOAuth(provider, {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      toast.error(`${label} sign-in failed`);
      return;
    }
    // Mark before redirect — provider typically navigates away; flag will be
    // set when this same device returns post-OAuth.
    markReturning();
    if (result.redirected) return;
    toast.success(`Signed in with ${label}!`);
    handleClose();
  };

  return (
    <AnimatePresence>
      {isOpenResolved && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="relative w-full max-w-sm mx-4 rounded-2xl border border-border bg-card p-6"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={handleClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>

            <h2 className="font-mono font-bold text-lg text-foreground mb-1">
              {mode === "login" ? "Welcome back, hacker" : "Invite required"}
            </h2>
            <p className="text-xs text-muted-foreground font-mono mb-5">
              {mode === "login"
                ? "Pick up where you left off — points, streaks, and all."
                : "HackerLabs is invite-only. Got a code? Continue below."}
            </p>

            {/* Google */}
            <button
              onClick={() => handleOAuth("google")}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-border bg-secondary hover:bg-secondary/80 transition-colors font-mono text-sm text-foreground mb-2"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>

            {/* Apple */}
            <button
              onClick={() => handleOAuth("apple")}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-border bg-foreground text-background hover:bg-foreground/90 transition-colors font-mono text-sm mb-4"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 12.04c-.03-3.02 2.47-4.47 2.58-4.55-1.41-2.06-3.6-2.34-4.38-2.37-1.86-.19-3.63 1.1-4.58 1.1-.96 0-2.41-1.07-3.97-1.04-2.04.03-3.93 1.19-4.98 3.02-2.13 3.69-.54 9.13 1.51 12.13 1.01 1.46 2.21 3.1 3.78 3.04 1.52-.06 2.09-.98 3.92-.98 1.83 0 2.35.98 3.95.95 1.63-.03 2.66-1.49 3.66-2.96 1.16-1.7 1.63-3.35 1.66-3.43-.04-.02-3.18-1.22-3.21-4.85zM14.31 3.34c.84-1.02 1.41-2.43 1.25-3.84-1.21.05-2.68.81-3.55 1.82-.78.9-1.46 2.34-1.28 3.71 1.35.1 2.73-.69 3.58-1.69z"/></svg>
              Continue with Apple
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[10px] font-mono text-muted-foreground">OR</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Email form (login only) — signup is invite-gated, see /signup */}
            {mode === "signup" ? (
              <button
                onClick={() => { handleClose(); navigate("/signup"); }}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground font-mono text-sm font-semibold hover:bg-primary/90"
              >
                <ShieldCheck className="w-4 h-4" /> Continue with invite code
              </button>
            ) : (
            <form onSubmit={handleEmail} className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  required
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-secondary text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="password"
                  required
                  minLength={6}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-secondary text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-mono text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {loading ? "..." : "Sign In"}
              </button>
            </form>
            )}

            <p className="text-center text-xs font-mono text-muted-foreground mt-4">
              {mode === "login" ? (
                <>
                  Have an invite code?{" "}
                  <button
                    onClick={() => {
                      handleClose();
                      navigate("/signup");
                    }}
                    className="text-primary hover:underline"
                  >
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button onClick={() => setMode("login")} className="text-primary hover:underline">
                    Sign in
                  </button>
                </>
              )}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
